import {
  AccountTreeOutlined,
  DashboardOutlined,
  PeopleOutlined,
  RouterOutlined,
  TrendingUpOutlined,
} from '@mui/icons-material'
import { Box, Divider, Drawer, List } from '@mui/material'
import { useTranslation } from 'react-i18next'

import CustomerNavBarItems from '@/components/navigation/CustomerNavBarItems'
import { SIDEBAR_COLLAPSED_WIDTH, SIDEBAR_WIDTH } from '@/constants/layout'
import { USER_ROLES } from '@/constants/userRoles'
import environment from '@/environment'
import { useAuth } from '@/features/authentication/contexts/AuthContext'
import { usePermissions } from '@/features/authorization/contexts/PermissionsContext'
import { useFeatureToggle } from '@/features/featureToggle/contexts/FeatureToggleContext'

import CollapsibleNavSection from './CollapsibleNavSection'
import ExperimentsSection from './ExperimentsSection'
import ListItemLink from './ListItemLink'

type NavBarProps = {
  drawerWidth: number
  isCollapsed: boolean
  sidebarEasing: string
  sidebarTransitionDuration: number
}

function NavBar({ drawerWidth, isCollapsed, sidebarEasing, sidebarTransitionDuration }: Readonly<NavBarProps>) {
  const { t } = useTranslation()
  const { isEnabled } = useFeatureToggle()
  const { permissions } = usePermissions()
  const { loggedInUser } = useAuth()

  const hasCustomerUserRole = loggedInUser?.role === USER_ROLES.RESOURCE_OWNERS.value
  const hasCustomerManagerUserRole = loggedInUser?.role === USER_ROLES.CUSTOMER_MANAGERS.value

  const isBessDayAheadEnabled =
    isEnabled('FLEX_PORTAL_BID_CASCADING_MFRR_ENABLED') ||
    isEnabled('FLEX_PORTAL_BID_CASCADING_FCRD_ENABLED') ||
    isEnabled('FLEX_PORTAL_BID_CASCADING_FCRN_ENABLED')

  return (
    <Drawer
      sx={(theme) => ({
        width: drawerWidth,
        flexShrink: 0,
        height: '100%',
        transition: `width ${sidebarTransitionDuration}ms ${sidebarEasing}`,
        '@media (prefers-reduced-motion: reduce)': {
          transition: 'none',
        },
        '& .MuiDrawer-paper': {
          position: 'relative',
          width: isCollapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH,
          boxSizing: 'border-box',
          overflowX: 'hidden',
          overflowY: 'hidden',
          backgroundColor: theme.palette.background.paper,
          borderRight: `1px solid ${theme.palette.divider}`,
          transition: `width ${sidebarTransitionDuration}ms ${sidebarEasing}`,
          '@media (prefers-reduced-motion: reduce)': {
            transition: 'none',
          },
        },
      })}
      variant="permanent"
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box
          sx={(theme) => ({
            flex: 1,
            overflowY: 'auto',
            overflowX: 'hidden',
            pt: 1,
            scrollbarWidth: 'thin',
            scrollbarColor: `${theme.palette.action.disabled} transparent`,
            '&::-webkit-scrollbar': {
              width: '6px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: theme.palette.action.disabled,
              borderRadius: '3px',
              '&:hover': {
                backgroundColor: theme.palette.action.active,
              },
            },
          })}
        >
          <List component="nav">
            {hasCustomerUserRole ? (
              <CustomerNavBarItems isCollapsed={isCollapsed} />
            ) : (
              <>
                {!hasCustomerManagerUserRole && (
                  <ListItemLink
                    activePath={'/'}
                    icon={<DashboardOutlined />}
                    isCollapsed={isCollapsed}
                    primary={t('component.navbar.dashboard')}
                    to="/"
                  />
                )}

                <CollapsibleNavSection
                  defaultExpanded
                  icon={<TrendingUpOutlined />}
                  isCollapsed={isCollapsed}
                  title={t('component.navbar.section_bidding_trading')}
                >
                  {permissions.has('bidding') && (
                    <ListItemLink
                      activePath={(pathname) =>
                        pathname.startsWith('/bidding') && !pathname.startsWith('/bidding/offer/bess/day-ahead')
                      }
                      primary={t('component.navbar.bidding')}
                      to="/bidding"
                    />
                  )}

                  {isBessDayAheadEnabled && permissions.has('bidding') && (
                    <ListItemLink
                      activePath={'/bidding/offer/bess/day-ahead'}
                      primary={t('component.navbar.bess_day_ahead_bid')}
                      to="/bidding/offer/bess/day-ahead"
                    />
                  )}

                  {isEnabled('FLEX_PORTAL_ACTIVATION_PLAN_PAGE_ENABLED') && permissions.has('bidding') && (
                    <ListItemLink
                      activePath={'/activation-plan/*'}
                      primary={t('component.navbar.activation_plan')}
                      to="/activation-plan"
                    />
                  )}

                  {permissions.has('wholesale') && (
                    <ListItemLink
                      activePath={'/wholesale/*'}
                      primary={t('component.navbar.wholesale')}
                      to="/wholesale"
                    />
                  )}

                  {isEnabled('FLEX_PORTAL_DISTURBANCES_PAGE_ENABLED') && permissions.has('disturbances') && (
                    <ListItemLink
                      activePath={'/disturbances/*'}
                      primary={t('component.navbar.disturbances')}
                      to="/disturbances"
                    />
                  )}

                  {permissions.has('bessDashboard') && (
                    <ListItemLink
                      activePath={'/bess-dashboard'}
                      primary={t('common.bess_forecasts')}
                      to="/bess-dashboard"
                    />
                  )}

                  {permissions.has('reports') && (
                    <ListItemLink activePath={'/reports'} primary={t('component.navbar.reports')} to="/reports" />
                  )}
                </CollapsibleNavSection>

                {isEnabled('PORTFOLIO_HIERARCHY_PAGE_ENABLED') && permissions.has('portfolioHierarchy') && (
                  <CollapsibleNavSection
                    defaultExpanded
                    icon={<AccountTreeOutlined />}
                    isCollapsed={isCollapsed}
                    title={t('component.navbar.portfolio_hierarchy')}
                  >
                    <ListItemLink
                      activePath={'/portfolio-hierarchy/portfolios'}
                      primary={t('component.navbar.portfolios')}
                      to="/portfolio-hierarchy/portfolios"
                    />
                    <ListItemLink
                      activePath={(pathname) => pathname.startsWith('/portfolio-hierarchy/regulation-objects')}
                      primary={t('component.navbar.regulation_objects')}
                      to="/portfolio-hierarchy/regulation-objects"
                    />
                    <ListItemLink
                      activePath={'/portfolio-hierarchy/activation-groups'}
                      primary={t('component.navbar.activation_groups')}
                      to="/portfolio-hierarchy/activation-groups"
                    />
                  </CollapsibleNavSection>
                )}

                <CollapsibleNavSection
                  defaultExpanded
                  icon={<RouterOutlined />}
                  isCollapsed={isCollapsed}
                  title={t('component.navbar.section_asset_management')}
                >
                  {permissions.has('activationGroups') && (
                    <ListItemLink
                      activePath={'/resources/*'}
                      primary={t('component.navbar.resources')}
                      to="/resources"
                    />
                  )}

                  {permissions.has('controllers') && (
                    <ListItemLink
                      activePath={'/controllers/*'}
                      primary={t('component.navbar.controllers')}
                      to="/controllers"
                    />
                  )}

                  {permissions.has('prequalifications') && (
                    <ListItemLink
                      activePath={'/prequalifications/*'}
                      primary={t('component.navbar.prequalifications')}
                      to="/prequalifications/local"
                    />
                  )}

                  {permissions.has('resourceSwitchboard') && (
                    <ListItemLink
                      activePath={'/resource-switchboard'}
                      primary={t('component.navbar.resource_switchboard')}
                      to="/resource-switchboard"
                    />
                  )}
                </CollapsibleNavSection>

                <CollapsibleNavSection
                  defaultExpanded
                  icon={<PeopleOutlined />}
                  isCollapsed={isCollapsed}
                  title={t('component.navbar.section_customers')}
                >
                  {permissions.has('siteDetails') && (
                    <ListItemLink activePath={'/sites/*'} primary={t('component.navbar.sites')} to="/sites" />
                  )}

                  {permissions.has('partners') && (
                    <ListItemLink activePath={'/partners/*'} primary={t('component.navbar.partners')} to="/partners" />
                  )}

                  {permissions.has('users') && (
                    <ListItemLink activePath={'/users/*'} primary={t('component.navbar.users')} to="/users" />
                  )}

                  {permissions.has('flexBalance') && (
                    <ListItemLink activePath={'/flexbalance'} primary="FlexBalance" to="/flexbalance" />
                  )}
                </CollapsibleNavSection>

                {environment.environmentName !== 'production' && !isCollapsed && (
                  <>
                    <Divider sx={{ marginTop: 2 }} />
                    <ExperimentsSection />
                  </>
                )}
              </>
            )}
          </List>
        </Box>
      </Box>
    </Drawer>
  )
}

export default NavBar
